import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {DomainType, MultiDomainProfile} from 'ngx-heka-design-lib';
import {BehaviorSubject, map, Observable, tap} from 'rxjs';
import {environment} from 'src/environments/local/environment';
import {CompanyData, CompanyDataCreateDTO, UsagePreferencesRequestData} from '../models/company-data';
import {L1L3AuthService} from 'ngx-auth-util-lib';
import {DomainHandlerService} from './domain-handler-service';

/**
 * Similar to other app's but intentionally not in library because of routing reason.
 */
@Injectable({
  providedIn: 'root'
})
export class MultiDomainMgmtService {
  private sharedMultiDomainProfile = new BehaviorSubject<MultiDomainProfile | null>(null);

  constructor(
    private http: HttpClient,
    private authService: L1L3AuthService,
    private domainHandlerService: DomainHandlerService
  ) {
  }

  getSharedMultiDomainProfile(observableOnly: boolean = false) {
    if (this.sharedMultiDomainProfile.value === null && !observableOnly) {
      this.updateMultiDomainProfile();
    }
    return this.sharedMultiDomainProfile.asObservable();
  }

  getSharedMultiDomainProfileValue(): MultiDomainProfile | null {
    return this.sharedMultiDomainProfile.value;
  }

  updateMultiDomainProfile() {
    this.getMultiDomainProfile().subscribe({
      next: profile => {
        this.sharedMultiDomainProfile.next(profile);
      },
      error: err => {
        this.sharedMultiDomainProfile.next(null);
        this.sharedMultiDomainProfile.error(err);
      }
    });
  }

  resetMultiDomainProfile(): void {
    this.sharedMultiDomainProfile.next(null);
  }

  getMultiDomainProfile(): Observable<MultiDomainProfile> {
    return this.http
      .get<MultiDomainProfile>(
        environment.backend.endpoint.host +
        environment.domains.endpoint.paths.prefix +
        environment.domains.endpoint.paths.profile
      )
      .pipe(
        map(profile => ({
          ...profile,
          domains: [
            ...profile.domains
              ?.filter(d => d.domainType == DomainType.INVITED)
              ?.sort((a, b) => b.invitationTimestamp - a.invitationTimestamp),
            ...profile.domains
              ?.filter(d => d.domainType != DomainType.INVITED)
              ?.sort((a, b) => a.companyName?.localeCompare(b.companyName))
          ]
        })),
        tap(profile => {
          const userInfo = this.authService.getUserInfo();

          if (userInfo?.domainId) {
            const foundDomain = profile.domains.find(d => d.domainId == userInfo.domainId);

            if (!foundDomain) {
              this.authService.clearL3Token();
              if (profile.domains.length > 1) {
                window.location.href = environment.links.applications.hcp;
              } else {
                this.domainHandlerService.reloadApplication();
              }
            } else if (!foundDomain.active) {
              this.authService.clearL3Token();
              this.domainHandlerService.reloadApplication();
            }
          }
        })
      );
  }

  createCompanyData(newCompanyData: CompanyData): Observable<CompanyDataCreateDTO> {
    const sourceTabId = this.authService.sourceTabId;
    let params = new HttpParams();

    if (sourceTabId) {
      params = params.append('sourceTabId', sourceTabId);
    }

    return this.http.post<CompanyDataCreateDTO>(
      environment.backend.endpoint.host + environment.domains.endpoint.paths.prefix,
      newCompanyData,
      {params}
    );
  }

  createDomainUsagePreferences(
    domainId: string,
    usagePreferencesRequestData: UsagePreferencesRequestData
  ): Observable<CompanyData> {
    return this.http.post<CompanyData>(
      environment.backend.endpoint.host +
      environment.domains.endpoint.paths.prefix +
      `/${domainId}` +
      environment.domains.endpoint.paths.usage,
      usagePreferencesRequestData
    );
  }

  acceptInvitation(domainId: string) {
    return this.http.put<any>(
      environment.backend.endpoint.host +
      environment.domains.endpoint.paths.prefix +
      `/${domainId}` +
      environment.domains.endpoint.paths.invitation +
      environment.domains.endpoint.paths.accept,
      {}
    );
  }

  rejectInvitation(domainId: string) {
    return this.http.put<any>(
      environment.backend.endpoint.host +
      environment.domains.endpoint.paths.prefix +
      `/${domainId}` +
      environment.domains.endpoint.paths.invitation +
      environment.domains.endpoint.paths.reject,
      {}
    );
  }

  leaveDomain(domainId: string, electedDomainId?: string) {
    const sourceTabId = this.authService.sourceTabId;
    let params = new HttpParams();

    if (sourceTabId) {
      params = params.append('sourceTabId', sourceTabId);
    }

    return this.http.post<any>(
      environment.backend.endpoint.host +
      environment.domains.endpoint.paths.prefix +
      `/${domainId}` +
      environment.domains.endpoint.paths.leave,
      {
        electedDomainId
      },
      {
        params
      }
    );
  }
}
